import React from 'react';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';
import TermsHero from './components/TermsHero';
import TermsTableOfContents from './components/TermsTableOfContents';
import AcceptanceServiceSection from './components/AcceptanceServiceSection';
import AccountSubscriptionSection from './components/AccountSubscriptionSection';
import PaymentIPSection from './components/PaymentIPSection';
import LiabilityWarrantySection from './components/LiabilityWarrantySection';
import DisputeContactSection from './components/DisputeContactSection';
import Breadcrumb from '../../components/Breadcrumb';
import TermsRelatedPages from './components/TermsRelatedPages';

const TermsConditionsPage = () => {
  return (
    <>
      <Helmet>
        <title>Vilkår & Betingelser — NordPass AS</title>
        <meta name="description" content="NordPass sine brukervilkår og tjenestebetingelser. Les om aksept av vilkår, abonnementsvilkår, betalingsbetingelser, ansvarsbegrensning og norsk jurisdiksjon." />
        <link
          href="https://fonts.googleapis.com/css2?family=Syne:wght@400;600;700;800&family=Lora:ital,wght@0,400;0,700;1,400;1,700&family=Inter:wght@300;400;500;600&family=JetBrains+Mono:wght@400;500;700&display=swap"
          rel="stylesheet"
        />
      </Helmet>
      <div style={{ background: '#050A12', minHeight: '100vh', overflowX: 'hidden' }}>
        {/* Sticky nav */}
        <nav
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            zIndex: 100,
            background: 'rgba(5,10,18,0.92)',
            backdropFilter: 'blur(16px)',
            borderBottom: '1px solid rgba(255,255,255,0.06)'
          }}
        >
          <div
            style={{
              maxWidth: '1400px',
              margin: '0 auto',
              padding: '0 5%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              height: '72px'
            }}
          >
            <Link
              to="/nord-pass-landing-page"
              style={{ display: 'flex', alignItems: 'center', gap: '10px', textDecoration: 'none' }}
            >
              <div
                style={{
                  width: '32px',
                  height: '32px',
                  background: '#3B6FD4',
                  clipPath: 'polygon(0 0, calc(100% - 8px) 0, 100% 8px, 100% 100%, 8px 100%, 0 calc(100% - 8px))'
                }}
              />
              <span
                style={{
                  fontFamily: 'Syne, sans-serif',
                  fontSize: '1.125rem',
                  fontWeight: 800,
                  color: '#EEF0F2',
                  letterSpacing: '-0.03em'
                }}
              >
                NordPass
              </span>
            </Link>

            <div style={{ display: 'flex', alignItems: 'center', gap: '32px' }}>
              {[
                { label: 'Hjem', href: '/nord-pass-landing-page' },
                { label: 'Tjenester', href: '/tjenester-services' },
                { label: 'Om Oss', href: '/om-oss-about-us' },
                { label: 'Kontakt', href: '/nord-pass-contact-page' },
              ]?.map((link, i) => (
                <Link
                  key={i}
                  to={link?.href}
                  style={{
                    fontFamily: 'Inter, sans-serif',
                    fontSize: '0.875rem',
                    color: 'rgba(238,240,242,0.65)',
                    textDecoration: 'none',
                    transition: 'color 150ms ease'
                  }}
                  onMouseEnter={e => { e.currentTarget.style.color = '#EEF0F2'; }}
                  onMouseLeave={e => { e.currentTarget.style.color = 'rgba(238,240,242,0.65)'; }}
                >
                  {link?.label}
                </Link>
              ))}
            </div>

            <Link
              to="/login"
              style={{
                fontFamily: 'Syne, sans-serif',
                fontSize: '0.8125rem',
                fontWeight: 700,
                color: '#EEF0F2',
                textDecoration: 'none',
                padding: '10px 20px',
                background: '#3B6FD4',
                letterSpacing: '0.06em',
                textTransform: 'uppercase'
              }}
            >
              Logg inn
            </Link>
          </div>
        </nav>

        {/* Breadcrumb */}
        <Breadcrumb
          crumbs={[
            { label: 'Hjem', href: '/nord-pass-landing-page' },
            { label: 'Juridisk', href: '/terms-conditions-vilk-r-betingelser' },
            { label: 'Vilkår & Betingelser' }
          ]}
        />

        {/* Hero */}
        <TermsHero />

        {/* Main layout */}
        <div
          style={{
            maxWidth: '1400px',
            margin: '0 auto',
            padding: '80px 5%',
            display: 'grid',
            gridTemplateColumns: '280px 1fr',
            gap: '60px',
            alignItems: 'start'
          }}
        >
          {/* Sticky sidebar TOC */}
          <aside style={{ position: 'sticky', top: '100px' }}>
            <TermsTableOfContents />

            {/* Legal badge */}
            <div
              style={{
                marginTop: '24px',
                background: 'rgba(249,115,22,0.06)',
                border: '1px solid rgba(249,115,22,0.2)',
                padding: '20px',
                textAlign: 'center'
              }}
            >
              <div
                style={{
                  fontFamily: 'Impact, sans-serif',
                  fontSize: '2rem',
                  color: '#F97316',
                  letterSpacing: '0.05em',
                  marginBottom: '6px'
                }}
              >
                NORSK LOV
              </div>
              <div
                style={{
                  fontFamily: 'JetBrains Mono, monospace',
                  fontSize: '0.625rem',
                  color: 'rgba(249,115,22,0.7)',
                  letterSpacing: '0.1em',
                  textTransform: 'uppercase'
                }}
              >
                OSLO TINGRETT · NORSK JURISDIKSJON
              </div>
            </div>

            {/* Privacy link */}
            <div style={{ marginTop: '16px' }}>
              <Link
                to="/privacy-policy-personvern"
                style={{
                  display: 'block',
                  background: 'rgba(59,111,212,0.06)',
                  border: '1px solid rgba(59,111,212,0.2)',
                  padding: '16px',
                  textDecoration: 'none',
                  textAlign: 'center',
                  transition: 'all 150ms ease'
                }}
                onMouseEnter={e => { e.currentTarget.style.background = 'rgba(59,111,212,0.12)'; }}
                onMouseLeave={e => { e.currentTarget.style.background = 'rgba(59,111,212,0.06)'; }}
              >
                <div style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: '0.625rem', color: '#3B6FD4', letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: '4px' }}>Se også</div>
                <div style={{ fontFamily: 'Syne, sans-serif', fontSize: '0.8125rem', fontWeight: 700, color: '#EEF0F2' }}>Personvernerklæring →</div>
              </Link>
            </div>
          </aside>

          {/* Main content */}
          <main>
            <AcceptanceServiceSection />
            <AccountSubscriptionSection />
            <PaymentIPSection />
            <LiabilityWarrantySection />
            <DisputeContactSection />

            {/* Related Pages */}
            <TermsRelatedPages />
          </main>
        </div>

        {/* Footer strip */}
        <div
          style={{
            borderTop: '1px solid rgba(255,255,255,0.06)',
            background: '#030710',
            padding: '32px 5%'
          }}
        >
          <div
            style={{
              maxWidth: '1400px',
              margin: '0 auto',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              flexWrap: 'wrap',
              gap: '16px'
            }}
          >
            <div
              style={{
                fontFamily: 'JetBrains Mono, monospace',
                fontSize: '0.6875rem',
                color: 'rgba(138,149,163,0.4)',
                letterSpacing: '0.06em'
              }}
            >
              © 2026 NordPass AS — Vilkår & Betingelser v1.0 — Sist oppdatert Februar 2026
            </div>
            <div style={{ display: 'flex', gap: '24px' }}>
              {[
                { label: 'Hjem', path: '/nord-pass-landing-page' },
                { label: 'Personvern', path: '/privacy-policy-personvern' },
                { label: 'Kontakt', path: '/nord-pass-contact-page' },
              ]?.map((item, i) => (
                <Link
                  key={i}
                  to={item?.path}
                  style={{
                    fontFamily: 'Inter, sans-serif',
                    fontSize: '0.75rem',
                    color: 'rgba(138,149,163,0.4)',
                    textDecoration: 'none',
                    transition: 'color 150ms ease'
                  }}
                  onMouseEnter={e => { e.currentTarget.style.color = '#8A95A3'; }}
                  onMouseLeave={e => { e.currentTarget.style.color = 'rgba(138,149,163,0.4)'; }}
                >
                  {item?.label}
                </Link>
              ))}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default TermsConditionsPage;
